import { useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';

interface ToastOptions {
  duration?: number;
}

export function useAppToast() {
  // Memoizar cada método individualmente para garantir referência estável
  const success = useCallback((message: string, options?: ToastOptions) => {
    console.log('🟢 Toast Success:', message, options);
    toast.success(message, {
      duration: 3000,
      ...options,
    });
  }, []);

  const error = useCallback((message: string | Error, options?: ToastOptions) => {
    console.error('🔴 Toast Error:', message, options);
    // Log stack trace if message is an Error object
    if (message instanceof Error) {
      console.error('🔴 Error Stack:', message.stack);
    }
    // Convert Error to string for toast
    const errorMessage = message instanceof Error ? message.message : message;
    toast.error(errorMessage, {
      duration: 4000,
      ...options,
    });
  }, []);

  const warning = useCallback((message: string, options?: ToastOptions) => {
    console.warn('🟡 Toast Warning:', message, options);
    toast(message, {
      duration: 3000,
      icon: '⚠️',
      ...options,
    });
  }, []);

  const info = useCallback((message: string, options?: ToastOptions) => {
    console.info('🔵 Toast Info:', message, options);
    toast(message, {
      duration: 3000,
      icon: 'ℹ️',
      ...options,
    });
  }, []);

  const promise = useCallback(<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string;
      error: string;
    },
    options?: ToastOptions
  ) => {
    console.log('🔄 Toast Promise Started:', messages);
    return toast.promise(
      promise.catch((error) => {
        console.error('🔴 Toast Promise Error:', error);
        // Log full error details
        if (error instanceof Error) {
          console.error('🔴 Promise Error Details:', {
            name: error.name,
            message: error.message,
            stack: error.stack
          });
        }
        throw error;
      }),
      {
        loading: messages.loading,
        success: (data) => {
          console.log('🟢 Toast Promise Success:', messages.success, data);
          return messages.success;
        },
        error: (error) => {
          console.error('🔴 Toast Promise Error Handler:', error);
          return messages.error;
        },
      },
      options
    );
  }, []);

  // Memoizar o objeto retornado para garantir referência estável
  return useMemo(() => ({
    success,
    error,
    warning,
    info,
    promise,
  }), [success, error, warning, info, promise]);
}
